import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { reservationsApi, roomsApi, roomTypesApi, guestsApi, invoicesApi, paymentsApi, billSettingsApi } from '../../services/api';
import { calcBill, calcNights, fmt, getCurrentTimeStr, getMinBackdateStr, BACKDATE_DAYS, ID_TYPES, PAYMENT_TYPES, formatAadhaar, formatPAN } from '../../utils/billing';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { useConfirm, useNotify } from '../../context/ConfirmContext';

export default function ReservationsPage({ hotel, onSuccess, initialEditId, clearSelection, onBack }) {
    const notify = useNotify();
    const confirm = useConfirm();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const [reservations, setReservations] = useState([]);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [editingRes, setEditingRes] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editingPayments, setEditingPayments] = useState([]);
    const [correctingPaymentId, setCorrectingPaymentId] = useState(null);
    const [correctionDraft, setCorrectionDraft] = useState(null); // { type, last5, note }
    // Admin-only correction of a billed stay's amounts: null, or { reason } while active.
    const [billCorrection, setBillCorrection] = useState(null);
    // How the admin settles a billed stay whose payments no longer match its total.
    const [balanceAdjust, setBalanceAdjust] = useState({ type: 'cash', last5: '', saving: false });
    const [rooms, setRooms] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [confirmModal, setConfirmModal] = useState(null); // { icon, title, message, onConfirm, type }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- load is redefined every render
    useEffect(() => { load(); }, [hotel]);

    useEffect(() => {
        if (!initialEditId) return;
        void openReservationForEdit(initialEditId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialEditId, reservations]);

    const mapReservation = (r) => ({
        ...r,
        guestId: r.guestId || r.guest_id,
        guestName: r.guestName || r.guest_name,
        guestPhone: r.guestPhone || r.guest_phone,
        guestSalutation: r.guestSalutation || r.guest_salutation || '',
        guestGender: r.guestGender || r.guest_gender || '',
        guestEmail: r.guestEmail || r.guest_email || '',
        guestNationality: r.guestNationality || r.guest_nationality || '',
        guestDob: r.guestDob || r.guest_dob || '',
        guestAddress: r.guestAddress || r.guest_address || '',
        guestIdType: r.guestIdType || r.guest_id_type || '',
        guestIdNumber: r.guestIdNumber || r.guest_id_number || '',
        guestCustomerType: r.guestCustomerType || r.guest_customer_type || '',
        checkIn: r.checkIn || r.check_in,
        checkOut: r.checkOut || r.check_out,
        checkInTime: r.checkInTime || r.check_in_time,
        checkOutTime: r.checkOutTime || r.check_out_time,
        roomType: r.roomType || r.room_type,
        customerType: r.customerType || r.customer_type || 'individual',
        company: r.company || r.guest_company || '',
        companyGst: r.companyGst || r.company_gst || r.guest_company_gst || '',
        totalAmount: Number(r.totalAmount ?? r.total_amount ?? 0),
        advancePaid: Number(r.advancePaid ?? r.advance_paid ?? 0),
        roomRate: Number(r.roomRate ?? r.room_rate ?? 0),
        extraCharges: Number(r.extraCharges ?? r.extra_charges ?? 0),
        damageCharges: Number(r.damageCharges ?? r.damage_charges ?? 0),
        damageNote: r.damageNote || r.damage_note || '',
        notes: r.notes || '',
        roomConfigs: r.roomConfigs
            ? r.roomConfigs
            : (r.room_configs ? (typeof r.room_configs === 'string' ? JSON.parse(r.room_configs) : r.room_configs) : [])
    });

    const getDefaultRoomConfigs = (r) => {
        const existingConfigs = r.roomConfigs && r.roomConfigs.length > 0 ? r.roomConfigs : null;
        if (existingConfigs) {
            return existingConfigs.map((config, idx) => ({
                id: config.id || `${r.id || 'res'}-${idx}`,
                room: config.room || '',
                roomType: config.roomType || config.room_type || '',
                roomRate: Number(config.roomRate ?? config.room_rate ?? 0),
                roomsCount: Number(config.roomsCount ?? config.rooms_count ?? ((config.room || '').split(',').filter(Boolean).length || 1)),
                offeredPrice: Number(config.offeredPrice ?? config.offered_price ?? config.roomRate ?? config.room_rate ?? 0),
                basePrice: Number(config.basePrice ?? config.base_price ?? config.offeredPrice ?? config.offered_price ?? config.roomRate ?? config.room_rate ?? 0),
                extraBeds: Number(config.extraBeds ?? config.extra_beds ?? 0),
                capacityPreference: config.capacityPreference || config.capacity_preference || 'extra_bed'
            }));
        }
        return [
            {
                id: Date.now(),
                room: r.room || '',
                roomType: r.roomType || '',
                roomRate: Number(r.roomRate) || 0,
                roomsCount: (r.room ? r.room.split(',').length : 1),
                offeredPrice: Number(r.roomRate) || 0,
                basePrice: Number(r.roomRate) || 0,
                extraBeds: 0,
                capacityPreference: 'extra_bed'
            }
        ];
    };

    const buildEditableReservation = (reservation, guest = null) => {
        const mappedReservation = mapReservation(reservation);
        return {
            ...mappedReservation,
            originalAdvancePaid: Number(mappedReservation.advancePaid) || 0,
            originalCheckIn: mappedReservation.checkIn ? String(mappedReservation.checkIn).split('T')[0] : '',
            advancePayType: 'cash',
            advanceLast5: '',
            advanceNote: '',
            roomConfigs: getDefaultRoomConfigs(mappedReservation),
            guestForm: {
                id: guest?.id || mappedReservation.guestId || '',
                salutation: guest?.salutation || mappedReservation.guestSalutation || 'Mr.',
                name: guest?.name || mappedReservation.guestName || '',
                gender: guest?.gender || mappedReservation.guestGender || 'Male',
                phone: guest?.phone || mappedReservation.guestPhone || '',
                email: guest?.email || mappedReservation.guestEmail || '',
                nationality: guest?.nationality || mappedReservation.guestNationality || 'Indian',
                dob: guest?.dob ? String(guest.dob).split('T')[0] : (mappedReservation.guestDob ? String(mappedReservation.guestDob).split('T')[0] : ''),
                customerType: guest?.customer_type || mappedReservation.guestCustomerType || mappedReservation.customerType || 'individual',
                idType: guest?.id_type || mappedReservation.guestIdType || mappedReservation.id_type || mappedReservation.idType || '',
                idNumber: (() => {
                  const rawId = guest?.id_number || mappedReservation.guestIdNumber || mappedReservation.id_number || mappedReservation.idNumber || '';
                  const idType = guest?.id_type || mappedReservation.guestIdType || mappedReservation.id_type || mappedReservation.idType || '';
                  return idType === 'Aadhaar Card' ? formatAadhaar(rawId) : rawId;
                })(),
                address: guest?.address || mappedReservation.guestAddress || '',
                company: guest?.company || mappedReservation.company || '',
                companyGst: guest?.company_gst || mappedReservation.companyGst || ''
            }
        };
    };

    const mergeGuestIntoEditableReservation = (reservationState, guest) => ({
        ...reservationState,
        customerType: guest?.customer_type || reservationState.customerType || 'individual',
        company: guest?.company || reservationState.company || '',
        companyGst: guest?.company_gst || reservationState.companyGst || '',
        guestForm: {
            ...reservationState.guestForm,
            id: guest?.id || reservationState.guestForm.id,
            salutation: guest?.salutation || reservationState.guestForm.salutation,
            name: guest?.name || reservationState.guestForm.name,
            gender: guest?.gender || reservationState.guestForm.gender,
            phone: guest?.phone || reservationState.guestForm.phone,
            email: guest?.email || reservationState.guestForm.email,
            nationality: guest?.nationality || reservationState.guestForm.nationality,
            dob: guest?.dob ? String(guest.dob).split('T')[0] : reservationState.guestForm.dob,
            customerType: guest?.customer_type || reservationState.guestForm.customerType,
            idType: guest?.id_type || reservationState.guestForm.idType,
            idNumber: (() => {
              const rawId = guest?.id_number || reservationState.guestForm.idNumber || '';
              const idType = guest?.id_type || reservationState.guestForm.idType || '';
              return idType === 'Aadhaar Card' ? formatAadhaar(rawId) : rawId;
            })(),
            address: guest?.address || reservationState.guestForm.address,
            company: guest?.company || reservationState.guestForm.company,
            companyGst: guest?.company_gst || reservationState.guestForm.companyGst
        }
    });

    const openReservationForEdit = async (reservationIdOrRecord) => {
        const reservationId = typeof reservationIdOrRecord === 'string' ? reservationIdOrRecord : reservationIdOrRecord?.id;
        if (!reservationId) return;
        setEditLoading(true);
        try {
            // Always fetch fresh rather than trusting the page's cached list —
            // a room change, extension, or payment made elsewhere (e.g. the Room
            // Grid) since this list last loaded wouldn't be reflected, and saving
            // over that stale snapshot silently reverts those changes.
            const reservation = mapReservation(await reservationsApi.get(reservationId));
            const initialState = buildEditableReservation(reservation);
            setEditingRes(initialState);
            if (clearSelection) clearSelection();

            setCorrectingPaymentId(null);
            setBillCorrection(null);
            setBalanceAdjust({ type: 'cash', last5: '', saving: false });
            paymentsApi.forReservation(reservationId).then(setEditingPayments).catch(() => setEditingPayments([]));

            if (reservation.guestId || reservation.guestPhone) {
                try {
                    let guest = null;
                    if (guestsApi.get) {
                        guest = reservation.guestId ? await guestsApi.get(reservation.guestId) : null;
                    }
                    if (!guest && reservation.guestPhone) {
                        const matches = await guestsApi.list(reservation.guestPhone);
                        guest = matches.find(g => g.id === reservation.guestId) || matches.find(g => g.phone === reservation.guestPhone) || null;
                    }
                    if (guest) {
                        setEditingRes(prev => prev ? mergeGuestIntoEditableReservation(prev, guest) : prev);
                    }
                } catch (guestErr) {
                    console.warn('Guest details fallback used during edit:', guestErr);
                }
            }
        } catch (err) {
            await notify(err.message);
        } finally {
            setEditLoading(false);
        }
    };

    async function load() {
        if (!hotel) return;
        try {
            const [res, rms, rts] = await Promise.all([
                reservationsApi.list(hotel.id),
                roomsApi.list(hotel.id),
                roomTypesApi.list(hotel.id)
            ]);
            
            // Map snake_case from DB to camelCase for component
            const mapped = res.map(mapReservation);
            
            setReservations(mapped);
            setRooms(rms);
            setRoomTypes(rts);
        } catch (err) {
            console.error('Load Error:', err);
        }
    }

    const filtered = reservations.filter(r => {
        if (filter !== 'all' && r.status !== filter) return false;
        if (!search) return true;
        return r.guestName?.toLowerCase().includes(search.toLowerCase()) ||
            r.guestPhone?.includes(search) || r.room?.includes(search);
    });

    const handleCheckIn = async (resRecord) => {
        if (!resRecord) return;
        if (!resRecord.room || resRecord.room.trim() === '') {
            onSuccess?.('Room not assigned. Opening assignment window...', 'info');
            void openReservationForEdit(resRecord);
            return;
        }

        // Validate local room status before even calling API
        const roomNumbers = resRecord.room.split(',').map(s => s.trim()).filter(Boolean);
        for (const num of roomNumbers) {
            const roomObj = rooms.find(r => r.number === num);
            if (roomObj && roomObj.status === 'cleaning') {
                await notify(`Room ${num} is currently being cleaned. Mark it as clean in Dashboard before check-in.`);
                return;
            }
            if (roomObj && roomObj.status === 'occupied') {
                await notify(`Room ${num} is already occupied.`);
                return;
            }
        }

        setConfirmModal({
            icon: '🛎️',
            title: 'Check-in Guest?',
            message: `Do you want to check-in ${resRecord.guestName} to Room ${resRecord.room} now?`,
            onConfirm: async () => {
                try {
                    await reservationsApi.update(resRecord.id, { 
                        status: 'checked-in',
                        check_in_time: getCurrentTimeStr() 
                    });
                    setConfirmModal(null);
                    await load();
                } catch (err) {
                    await notify('Error: ' + err.message);
                }
            }
        });
    };

    const handleDelete = async (resId) => {
        setConfirmModal({
            icon: '🗑️',
            title: 'Delete Reservation?',
            message: 'Are you sure you want to permanently delete this reservation record?',
            type: 'danger',
            confirmText: 'Delete Now',
            onConfirm: async () => {
                try {
                    await reservationsApi.delete(resId);
                    setConfirmModal(null);
                    load();
                } catch (err) { await notify(err.message); }
            }
        });
    };

    const handleEditClick = (r) => {
        void openReservationForEdit(r);
    };

    const handleConfigChange = (id, updates) => {
        setEditingRes(prev => ({
            ...prev,
            roomConfigs: prev.roomConfigs.map(c => c.id === id ? { ...c, ...updates } : c)
        }));
    };

    const addConfig = () => {
        const defaultType = roomTypes[0]?.name || '';
        const defaultPrice = roomTypes[0]?.base_price || 0;
        setEditingRes(prev => ({
            ...prev,
            roomConfigs: [...prev.roomConfigs, { id: Date.now(), roomType: defaultType, room: '', roomRate: defaultPrice, roomsCount: 1 }]
        }));
    };

    const removeConfig = (id) => {
        setEditingRes(prev => ({
            ...prev,
            roomConfigs: prev.roomConfigs.filter(c => c.id !== id)
        }));
    };

    const earliestEditableCheckIn = (r) => {
        const floor = getMinBackdateStr();
        return r?.originalCheckIn && r.originalCheckIn < floor ? r.originalCheckIn : floor;
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const isBilled = editingRes.status === 'checked-out';
            if (isBilled && billCorrection) {
                // Admin bill correction: only the amounts change; the server
                // recalculates GST/total, updates the invoice under the same
                // number and records the reason in the audit log.
                if (!billCorrection.reason.trim()) {
                    await notify('Enter the reason for correcting this bill.');
                    return;
                }
                const correctedRate = editingRes.roomConfigs.reduce((sum, c) => sum + (Number(c.roomRate) || 0) * (Number(c.roomsCount) || 1), 0);
                const updated = await reservationsApi.update(editingRes.id, {
                    notes: editingRes.notes,
                    roomRate: correctedRate,
                    roomConfigs: editingRes.roomConfigs,
                    extraCharges: Number(editingRes.extraCharges) || 0,
                    damageCharges: Number(editingRes.damageCharges) || 0,
                    damageNote: editingRes.damageNote,
                    correctionReason: billCorrection.reason.trim(),
                });
                setBillCorrection(null);
                setEditingRes(null);
                load();
                await notify({
                    icon: '✅',
                    title: 'Bill corrected',
                    body: `New bill total: ${fmt(updated?.total_amount)}. The invoice keeps its number — open the stay and use Re-print to print the corrected bill. If the payments no longer match the new total, the stay shows a "Balance after correction" box to correct the payment or record the refund.`,
                });
                return;
            }
            if (isBilled) {
                // Billed/completed reservations are locked for legal compliance. Only internal notes can be saved.
                await reservationsApi.update(editingRes.id, {
                    notes: editingRes.notes
                });
                setEditingRes(null);
                load();
                return;
            }

            const newCheckIn = editingRes.checkIn ? String(editingRes.checkIn).split('T')[0] : '';
            if (newCheckIn && newCheckIn !== editingRes.originalCheckIn && newCheckIn < earliestEditableCheckIn(editingRes)) {
                await notify(`Check-in date can be moved back by up to ${BACKDATE_DAYS} days only (earliest ${getMinBackdateStr()}).`);
                return;
            }

            const advanceDiff = Number(editingRes.advancePaid) - Number(editingRes.originalAdvancePaid);
            if (Math.abs(advanceDiff) > 0.001) {
                if (['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(editingRes.advancePayType) && !editingRes.advanceLast5) {
                    await notify('Enter the reference (last 5 digits) for this non-cash payment.');
                    return;
                }
            }

            const guestPayload = {
                salutation: editingRes.guestForm.salutation,
                name: editingRes.guestForm.name,
                gender: editingRes.guestForm.gender,
                phone: editingRes.guestForm.phone,
                email: editingRes.guestForm.email,
                nationality: editingRes.guestForm.nationality,
                dob: editingRes.guestForm.dob || null,
                customerType: editingRes.guestForm.customerType,
                idType: editingRes.guestForm.idType,
                idNumber: editingRes.guestForm.idNumber,
                address: editingRes.guestForm.address,
                company: editingRes.guestForm.customerType === 'corporate' ? editingRes.guestForm.company : '',
                companyGst: editingRes.guestForm.customerType === 'corporate' ? editingRes.guestForm.companyGst : ''
            };

            // Re-sync the room and roomType summary strings before sending
            const combinedRoom = editingRes.roomConfigs.map(c => c.room).filter(Boolean).join(', ');
            const combinedRoomType = editingRes.roomConfigs.map(c => c.roomType + (c.roomsCount > 1 ? ` x${c.roomsCount}` : '')).join(' + ');
            const totalRoomRate = editingRes.roomConfigs.reduce((sum, c) => sum + (Number(c.roomRate) || 0) * (Number(c.roomsCount) || 1), 0);

            const nights = calcNights(editingRes.checkIn, editingRes.checkOut) || 1;
            const bill = calcBill({
                roomRate: totalRoomRate,
                nights: nights,
                extraCharges: Number(editingRes.extraCharges),
                damageCharges: Number(editingRes.damageCharges)
            }, editingRes.billingType || (guestPayload.customerType === 'corporate' ? 'gst' : 'non-gst'), hotel?.gstRate);

            if (editingRes.guestId) {
                await guestsApi.update(editingRes.guestId, guestPayload);
            }

            await reservationsApi.update(editingRes.id, {
                room: combinedRoom,
                roomType: combinedRoomType,
                checkIn: editingRes.checkIn,
                checkOut: editingRes.checkOut,
                status: editingRes.status,
                nights: nights,
                roomRate: totalRoomRate,
                extraCharges: Number(editingRes.extraCharges),
                damageCharges: Number(editingRes.damageCharges),
                damageNote: editingRes.damageNote,
                advancePaid: Number(editingRes.advancePaid),
                advancePayType: editingRes.advancePayType,
                advanceLast5: editingRes.advanceLast5 || null,
                advanceNote: editingRes.advanceNote || null,
                totalAmount: bill.total,
                billingType: editingRes.billingType || (guestPayload.customerType === 'corporate' ? 'gst' : 'non-gst'),
                customerType: guestPayload.customerType,
                company: guestPayload.company,
                companyGst: guestPayload.companyGst,
                notes: editingRes.notes,
                roomConfigs: editingRes.roomConfigs
            });
            setEditingRes(null);
            load();
        } catch (err) { await notify(err.message); }
    };

    const statusColor = { 'checked-in': 'b-green', 'confirmed': 'b-blue', 'checked-out': 'b-gray', 'cancelled': 'b-red' };

    return (
        <div>
            <div className="panel">
                <div className="panel-head">
                    <span className="panel-title">All Reservations</span>
                    <div className="panel-actions">
                        <input
                            className="input"
                            style={{ width: 170 }}
                            placeholder="Search guest / room..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {['all', 'confirmed', 'checked-in', 'checked-out'].map(f => (
                            <button key={f} className={`btn btn-sm ${filter === f ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setFilter(f)}>
                                {f === 'all' ? 'All' : f.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="tbl-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Guest</th>
                                <th>Room</th>
                                <th>Check-in</th>
                                <th>Check-out</th>
                                <th>Type</th>
                                <th>Balance</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan={10}><div className="empty"><div className="empty-icon">📋</div>No reservations found</div></td></tr>
                            ) : filtered.map(r => {
                                const total = Number(r.totalAmount) || (Number(r.roomRate) * Number(r.nights || 1));
                                const balance = total - (Number(r.advancePaid) || 0);
                                return (
                                    <tr key={r.id}>
                                        <td>
                                            <div className="td-main">{r.guestName || '—'}</div>
                                            <div className="td-sub">{r.guestPhone}</div>
                                        </td>
                                        <td>{r.room}<div className="td-sub">{r.roomType}</div></td>
                                        <td>
                                            {r.checkIn}
                                            <div className="td-sub" style={{ fontSize: 10, color: 'var(--accent)' }}>
                                                {r.checkInTime || '—'}
                                            </div>
                                        </td>
                                        <td>
                                            {r.checkOut}
                                            <div className="td-sub" style={{ fontSize: 10, color: 'var(--danger)' }}>
                                                {r.checkOutTime || '—'}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge ${r.customerType === 'corporate' ? 'b-amber' : 'b-blue'}`}>
                                                {r.customerType === 'corporate' ? 'Corp' : 'Indiv'}
                                            </span>
                                        </td>
                                        <td style={{ color: balance > 0 ? 'var(--accent)' : 'var(--success)', fontWeight: 600 }}>
                                            {fmt(Math.max(0, balance))}
                                        </td>
                                        <td>
                                            <span className={`badge ${statusColor[r.status] || 'b-gray'}`}>
                                                {r.status.replace('-', ' ')}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                {r.status === 'confirmed' && (
                                                    <button className="btn btn-sm btn-gold" onClick={() => handleCheckIn(r)}>🛎️ Check-in</button>
                                                )}
                                                <button className="btn btn-sm btn-ghost" onClick={() => handleEditClick(r)}>✏️</button>
                                                <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(r.id)}>🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            {(editingRes || editLoading) && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: 820, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-head" style={{
                                position: 'sticky', top: 0, zIndex: 10,
                                background: 'var(--bg)', borderBottom: '1px solid var(--border)',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '14px 20px'
                            }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Edit Reservation</h3>
                            <button
                                type="button"
                                onClick={() => {
                                    setEditingRes(null);
                                    setEditLoading(false);
                                    if (initialEditId && onBack) onBack();
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 36, height: 36, borderRadius: '50%',
                                    border: '1.5px solid var(--border)',
                                    background: 'var(--s1)',
                                    color: 'var(--text)', fontSize: 18, fontWeight: 700,
                                    cursor: 'pointer', lineHeight: 1, flexShrink: 0
                                }}
                                title="Close"
                            >
                                ×
                            </button>
                        </div>
                        {editLoading && !editingRes ? (
                            <div style={{ padding: 24, color: 'var(--muted)' }}>Loading reservation details...</div>
                        ) : (
                        <form onSubmit={handleUpdate} style={{ padding: 20, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                            {(() => {
                                 const isBilled = editingRes.status === 'checked-out';
                                 const isCustomerLocked = isBilled && !isAdmin;
                                return (
                                    <>
                                        {isBilled && (
                                            <div style={{ padding: '12px 16px', background: isAdmin ? 'rgba(16,185,129,0.1)' : 'rgba(180,83,9,0.1)', border: `1px solid ${isAdmin ? 'rgba(16,185,129,0.25)' : 'rgba(180,83,9,0.25)'}`, borderRadius: 12, marginBottom: 16, fontSize: 12, color: isAdmin ? '#047857' : '#b45309', fontWeight: 700 }}>
                                                {isAdmin ? (
                                                    <span>🔓 <strong>Admin Access:</strong> As an Admin/Manager, you can update guest profile details (Name, Phone, ID, Address) to correct mistakes on this completed reservation.</span>
                                                ) : (
                                                    <span>🔒 <strong>Reservation Billed & Completed:</strong> Guest identity details (Name, Phone, ID, Address) are locked for Staff to prevent illegal record tampering. Only an Admin/Manager can edit guest profiles on billed reservations.</span>
                                                )}
                                            </div>
                                        )}
                                        {isBilled && isAdmin && !billCorrection && (() => {
                                            // Billed total vs money actually recorded for this stay.
                                            const paid = Math.round(editingPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) * 100) / 100;
                                            const billTotal = Number(editingRes.totalAmount) || 0;
                                            const diff = Math.round((paid - billTotal) * 100) / 100;
                                            if (Math.abs(diff) < 0.5) return null;
                                            const isRefund = diff > 0;
                                            const amount = Math.abs(diff);
                                            const needsRef = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(balanceAdjust.type);
                                            const record = async () => {
                                                if (needsRef && !balanceAdjust.last5) { await notify('Enter the reference (last 5 digits).'); return; }
                                                setBalanceAdjust(b => ({ ...b, saving: true }));
                                                try {
                                                    await paymentsApi.create({
                                                        reservationId: editingRes.id, hotelId: hotel.id, type: balanceAdjust.type,
                                                        amount: isRefund ? -amount : amount, last5: needsRef ? balanceAdjust.last5 : null,
                                                        note: isRefund ? 'Refund after bill correction' : 'Balance collected after bill correction',
                                                    });
                                                    setEditingPayments(await paymentsApi.forReservation(editingRes.id));
                                                    setBalanceAdjust({ type: 'cash', last5: '', saving: false });
                                                    onSuccess?.(isRefund ? `Refund of ${fmt(amount)} recorded.` : `${fmt(amount)} recorded.`);
                                                } catch (err) {
                                                    setBalanceAdjust(b => ({ ...b, saving: false }));
                                                    await notify(err.message);
                                                }
                                            };
                                            return (
                                                <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 12, border: '1.5px solid #b45309', background: 'rgba(245,158,11,0.07)' }}>
                                                    <div style={{ fontWeight: 800, fontSize: 13, color: '#b45309', marginBottom: 6 }}>⚖️ Balance after correction</div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span>Bill total</span><strong>{fmt(billTotal)}</strong></div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span>Payments recorded</span><strong>{fmt(paid)}</strong></div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                                                        <span>{isRefund ? 'Guest paid more than the bill' : 'Still due from guest'}</span>
                                                        <strong style={{ color: '#b45309' }}>{fmt(amount)}</strong>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: 'var(--muted)', margin: '8px 0' }}>
                                                        {isRefund
                                                            ? <>If the payment amount was only <strong>typed wrong</strong>, fix it with ✏️ Correct in Payment History below. If the guest <strong>really paid</strong> this much, record the refund given back:</>
                                                            : <>If the payment amount was only <strong>typed wrong</strong>, fix it with ✏️ Correct in Payment History below. If the guest still <strong>owes</strong> this, record it when collected:</>}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                                        <select className="input" value={balanceAdjust.type} onChange={e => setBalanceAdjust(b => ({ ...b, type: e.target.value, last5: '' }))} style={{ width: 150 }}>
                                                            {PAYMENT_TYPES.filter(pt => ['cash', 'upi', 'credit_card', 'debit_card', 'bank_transfer'].includes(pt.value)).map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                                                        </select>
                                                        {needsRef && <input className="input" style={{ width: 110 }} placeholder="Last 5 digits" maxLength={5} value={balanceAdjust.last5} onChange={e => setBalanceAdjust(b => ({ ...b, last5: e.target.value.slice(-5) }))} />}
                                                        <button type="button" className="btn btn-sm btn-gold" disabled={balanceAdjust.saving} onClick={record}>
                                                            {isRefund ? `Record Refund ${fmt(amount)}` : `Record Payment ${fmt(amount)}`}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        {isBilled && isAdmin && (() => {
                                            if (!billCorrection) {
                                                return (
                                                    <button type="button" className="btn btn-ghost" style={{ width: '100%', marginBottom: 16, border: '1px solid var(--danger)', color: 'var(--danger)', fontWeight: 800 }}
                                                        onClick={() => setBillCorrection({ reason: '' })}>
                                                        ✏️ Correct Bill Amounts (Admin)
                                                    </button>
                                                );
                                            }
                                            const correctedRate = editingRes.roomConfigs.reduce((sum, c) => sum + (Number(c.roomRate) || 0) * (Number(c.roomsCount) || 1), 0);
                                            const gstRate = Number(editingRes.gst_rate ?? hotel?.gstRate ?? 5);
                                            const preview = calcBill({
                                                roomRate: correctedRate, nights: Number(editingRes.nights) || 1,
                                                extraCharges: Number(editingRes.extraCharges) || 0, damageCharges: Number(editingRes.damageCharges) || 0,
                                            }, editingRes.billingType || editingRes.billing_type || 'non-gst', gstRate);
                                            const printed = Number(editingRes.totalAmount) || 0;
                                            return (
                                                <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 12, border: '1.5px solid var(--danger)', background: 'rgba(239,68,68,0.05)' }}>
                                                    <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--danger)', marginBottom: 6 }}>✏️ Correcting a billed stay</div>
                                                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
                                                        Room rate, extra charges and damage charges are now editable below. The invoice keeps the same number; dates, rooms and nights stay locked.
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span>Printed bill total</span><strong>{fmt(printed)}</strong></div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span>Corrected bill total{preview.gstAmount ? ` (incl. GST ${fmt(preview.gstAmount)})` : ''}</span><strong style={{ color: 'var(--danger)' }}>{fmt(preview.total)}</strong></div>
                                                    <div className="field" style={{ marginTop: 10, marginBottom: 8 }}>
                                                        <label>Reason for correction *</label>
                                                        <input value={billCorrection.reason} placeholder="e.g. Staff entered ₹2500 instead of ₹1500" onChange={e => setBillCorrection({ reason: e.target.value })} />
                                                    </div>
                                                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setBillCorrection(null); openReservationForEdit(editingRes.id); }}>Cancel correction</button>
                                                </div>
                                            );
                                        })()}
                                        <div style={{ background: 'var(--s1)', padding: 12, borderRadius: 12, marginBottom: 16 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 12 }}>CUSTOMER DETAILS</div>
                                            <div className="form-row-3">
                                                <div className="field">
                                                    <label>Salutation</label>
                                                    <select disabled={isCustomerLocked} value={editingRes.guestForm.salutation} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, salutation: e.target.value } }))}>
                                                        <option>Mr.</option>
                                                        <option>Ms.</option>
                                                        <option>Mrs.</option>
                                                        <option>Dr.</option>
                                                    </select>
                                                </div>
                                                <div className="field" style={{ flex: 2 }}>
                                                    <label>Full Name</label>
                                                    <input disabled={isCustomerLocked} value={editingRes.guestForm.name} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, name: e.target.value } }))} />
                                                </div>
                                                <div className="field">
                                                    <label>Gender</label>
                                                    <select disabled={isCustomerLocked} value={editingRes.guestForm.gender} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, gender: e.target.value } }))}>
                                                        <option>Male</option>
                                                        <option>Female</option>
                                                        <option>Other</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="form-row-2" style={{ marginTop: 12 }}>
                                                <div className="field">
                                                    <label>Phone</label>
                                                    <input disabled={isCustomerLocked} value={editingRes.guestForm.phone} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, phone: e.target.value } }))} />
                                                </div>
                                                <div className="field">
                                                    <label>Email</label>
                                                    <input disabled={isCustomerLocked} value={editingRes.guestForm.email || ''} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, email: e.target.value } }))} />
                                                </div>
                                            </div>
                                            <div className="form-row-2" style={{ marginTop: 12 }}>
                                                <div className="field">
                                                    <label>Nationality</label>
                                                    <input disabled={isCustomerLocked} value={editingRes.guestForm.nationality || ''} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, nationality: e.target.value } }))} />
                                                </div>
                                                <div className="field">
                                                    <label>Date of Birth</label>
                                                    <input disabled={isCustomerLocked} type="date" value={editingRes.guestForm.dob || ''} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, dob: e.target.value } }))} />
                                                </div>
                                            </div>
                                            <div className="field" style={{ marginTop: 12 }}>
                                                <label>Customer Type</label>
                                                <select disabled={isCustomerLocked} value={editingRes.guestForm.customerType} onChange={e => setEditingRes(prev => ({
                                                    ...prev,
                                                    customerType: e.target.value,
                                                    billingType: e.target.value === 'corporate' ? 'gst' : (prev.billingType === 'gst' ? 'non-gst' : prev.billingType),
                                                    guestForm: { ...prev.guestForm, customerType: e.target.value }
                                                }))}>
                                                    <option value="individual">Individual</option>
                                                    <option value="corporate">Corporate</option>
                                                </select>
                                            </div>
                                            {editingRes.guestForm.customerType === 'corporate' && (
                                                <div className="form-row-2" style={{ marginTop: 12 }}>
                                                    <div className="field">
                                                        <label>Company Name</label>
                                                        <input disabled={isCustomerLocked} value={editingRes.guestForm.company || ''} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, company: e.target.value } }))} />
                                                    </div>
                                                    <div className="field">
                                                        <label>Company GST Number</label>
                                                        <input disabled={isCustomerLocked} value={editingRes.guestForm.companyGst || ''} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, companyGst: e.target.value.toUpperCase() } }))} />
                                                    </div>
                                                </div>
                                            )}
                                            <div className="field" style={{ marginTop: 12 }}>
                                                <label>Address</label>
                                                <textarea disabled={isCustomerLocked} value={editingRes.guestForm.address || ''} onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, address: e.target.value } }))} />
                                            </div>
                                            <div className="form-row-2" style={{ marginTop: 12 }}>
                                                <div className="field">
                                                    <label>ID Type</label>
                                                    <select
                                                        disabled={isCustomerLocked}
                                                        value={editingRes.guestForm.idType || ''}
                                                        onChange={e => setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, idType: e.target.value } }))}
                                                    >
                                                        <option value="">-- Select ID Type --</option>
                                                        {ID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                </div>
                                                <div className="field">
                                                    <label>ID Number{editingRes.guestForm.idType === 'Aadhaar Card' && <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>xxxx-xxxx-xxxx</span>}</label>
                                                    <input
                                                        disabled={isCustomerLocked}
                                                        value={editingRes.guestForm.idNumber || ''}
                                                        onChange={e => {
                                                            const raw = e.target.value;
                                                            let formatted = raw;
                                                            if (editingRes.guestForm.idType === 'Aadhaar Card') {
                                                                formatted = formatAadhaar(raw);
                                                            } else if (editingRes.guestForm.idType === 'PAN Card') {
                                                                formatted = formatPAN(raw);
                                                            }
                                                            setEditingRes(prev => ({ ...prev, guestForm: { ...prev.guestForm, idNumber: formatted } }));
                                                        }}
                                                        placeholder={editingRes.guestForm.idType === 'Aadhaar Card' ? '1234-5678-9012' : editingRes.guestForm.idType === 'PAN Card' ? 'ABCDE1234F' : 'ID Number'}
                                                        maxLength={editingRes.guestForm.idType === 'Aadhaar Card' ? 14 : editingRes.guestForm.idType === 'PAN Card' ? 10 : undefined}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ background: 'var(--s1)', padding: 12, borderRadius: 12, marginBottom: 16 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 12 }}>ACCOMMODATIONS & ROOM TYPES</div>
                                            {editingRes.roomConfigs.map((config, idx) => (
                                                <div key={config.id} style={{ padding: 12, background: 'white', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 10, position: 'relative' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>Accommodation {idx + 1}</div>
                                                        {editingRes.roomConfigs.length > 1 && !isBilled && (
                                                            <button type="button" onClick={() => removeConfig(config.id)} style={{ padding: '2px 6px', fontSize: 10, background: 'none', border: 'none', color: 'var(--danger)', fontWeight: 700, cursor: 'pointer' }}>REMOVE</button>
                                                        )}
                                                    </div>
                                                    <div className="form-row-2">
                                                        <div className="field">
                                                            <label>Room Type</label>
                                                            <select disabled={isBilled} value={config.roomType} onChange={e => {
                                                                const rt = roomTypes.find(t => t.name === e.target.value);
                                                                handleConfigChange(config.id, { roomType: e.target.value, roomRate: rt?.base_price || 0, room: '' });
                                                            }}>
                                                                {roomTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                            </select>
                                                        </div>
                                                        <div className="field">
                                                            <label>Rooms Count</label>
                                                            <input disabled={isBilled} type="number" min="1" value={config.roomsCount} onChange={e => handleConfigChange(config.id, { roomsCount: parseInt(e.target.value) || 1, room: '' })} />
                                                        </div>
                                                    </div>
                                                    <div className="form-row-2" style={{ marginTop: 10 }}>
                                                        <div className="field">
                                                            <label>Room Number(s)</label>
                                                            <div style={{ position: 'relative' }}>
                                                                <input 
                                                                    readOnly 
                                                                    disabled={isBilled}
                                                                    value={config.room} 
                                                                    placeholder={`Select ${config.roomsCount} rooms...`}
                                                                    onClick={(e) => {
                                                                        if (isBilled) return;
                                                                        const el = e.target.nextSibling;
                                                                        if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                                                                    }}
                                                                />
                                                                {!isBilled && (
                                                                <div className="dropdown-multi" style={{ display: 'none', position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'white', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 8 }}>
                                                                    {(() => {
                                                                        const typeRooms = rooms.filter(r =>
                                                                            (r.room_type || r.roomType || '').toLowerCase() === (config.roomType || '').toLowerCase()
                                                                        );
                                                                        const alreadySelected = (config.room || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                        const vacantRooms = typeRooms.filter(r => r.status === 'vacant' || alreadySelected.includes(r.number));
                                                                        const dirtyRooms = typeRooms.filter(r => (r.status === 'cleaning' || r.status === 'dirty') && !alreadySelected.includes(r.number));

                                                                        return (
                                                                            <>
                                                                                {vacantRooms.length === 0 && dirtyRooms.length === 0 && (
                                                                                    <div style={{ padding: '8px 6px', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>No rooms available for this type</div>
                                                                                )}
                                                                                {vacantRooms.map(r => (
                                                                                    <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 13, userSelect: 'none' }}
                                                                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--s1)'}
                                                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={alreadySelected.includes(r.number)}
                                                                                            onChange={async e => {
                                                                                                const current = (config.room || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                                                let next;
                                                                                                if (e.target.checked) {
                                                                                                    if (current.length >= config.roomsCount) { await notify(`Limit exceeded. You can only select ${config.roomsCount} rooms.`); return; }
                                                                                                    next = [...current, r.number];
                                                                                                } else {
                                                                                                    next = current.filter(n => n !== r.number);
                                                                                                }
                                                                                                handleConfigChange(config.id, { room: next.join(', ') });
                                                                                            }}
                                                                                            style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
                                                                                        />
                                                                                        <span style={{ fontWeight: 600 }}>{r.number}</span>
                                                                                        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--success)', fontWeight: 600 }}>VACANT</span>
                                                                                    </label>
                                                                                ))}
                                                                                {dirtyRooms.length > 0 && (
                                                                                    <div style={{ marginTop: 6, padding: '8px 10px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 6 }}>
                                                                                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginBottom: 4 }}>
                                                                                            🧹 Dirty / Under Cleaning — Not available
                                                                                        </div>
                                                                                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                                                                                            {dirtyRooms.map(r => r.number).join(', ')} — clean these rooms before assigning
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="field">
                                                            <label>Rate / Room (₹)</label>
                                                            <input disabled={isBilled && !billCorrection} type="number" value={config.roomRate} onChange={e => handleConfigChange(config.id, { roomRate: e.target.value })} />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {!isBilled && (
                                                <button type="button" className="btn btn-sm btn-ghost" style={{ width: '100%', border: '1px dashed var(--border)', color: 'var(--accent)', fontWeight: 600 }} onClick={addConfig}>+ Add Another Room Type</button>
                                            )}
                                        </div>
                                        <div className="form-row-2" style={{ marginTop: 16 }}>
                                            <div className="field">
                                                <label>Check-in Date</label>
                                                <input
                                                    disabled={isBilled}
                                                    type="date"
                                                    // Can be moved back at most BACKDATE_DAYS (a missed entry), but
                                                    // the stay's own saved date always stays valid — a long stay
                                                    // that started earlier must not be forced forward on save.
                                                    min={earliestEditableCheckIn(editingRes)}
                                                    value={editingRes.checkIn ? editingRes.checkIn.split('T')[0] : ''}
                                                    onChange={e => setEditingRes({ ...editingRes, checkIn: e.target.value })}
                                                />
                                            </div>
                                            <div className="field">
                                                <label>Check-out Date</label>
                                                <input 
                                                    disabled={isBilled}
                                                    type="date"
                                                    min={editingRes.checkIn ? editingRes.checkIn.split('T')[0] : getMinBackdateStr()}
                                                    value={editingRes.checkOut ? editingRes.checkOut.split('T')[0] : ''} 
                                                    onChange={e => setEditingRes({ ...editingRes, checkOut: e.target.value })} 
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row-2" style={{ marginTop: 16 }}>
                                            <div className="field">
                                                <label>Total Rate/Night (Calculated ₹)</label>
                                                <input readOnly style={{ background: 'var(--s1)', fontWeight: 700 }} value={editingRes.roomConfigs.reduce((s, c) => s + (Number(c.roomRate) || 0) * (Number(c.roomsCount) || 1), 0)} />
                                            </div>
                                            <div className="field">
                                                <label>Extra Charges (₹)</label>
                                                <input disabled={isBilled && !billCorrection} type="number" value={editingRes.extraCharges} onChange={e => setEditingRes({ ...editingRes, extraCharges: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="form-row-2" style={{ marginTop: 16 }}>
                                            <div className="field">
                                                <label>Damage Charges (₹)</label>
                                                <input disabled={isBilled && !billCorrection} type="number" value={editingRes.damageCharges} onChange={e => setEditingRes({ ...editingRes, damageCharges: e.target.value })} />
                                            </div>
                                            <div className="field">
                                                <label>Advance Paid (₹)</label>
                                                <input disabled={isBilled} type="number" value={editingRes.advancePaid} onChange={e => setEditingRes({ ...editingRes, advancePaid: e.target.value })} />
                                            </div>
                                        </div>
                                        {Number(editingRes.advancePaid) !== Number(editingRes.originalAdvancePaid) && (
                                            <div style={{ marginTop: 12, background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', marginBottom: 8 }}>
                                                    {Number(editingRes.advancePaid) > Number(editingRes.originalAdvancePaid) ? '💳 How was the extra amount paid?' : '💳 How was the reduced amount refunded?'}
                                                </div>
                                                <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                                                    {PAYMENT_TYPES.map(pt => (
                                                        <button key={pt.value} type="button"
                                                            className={`pay-type-btn ${editingRes.advancePayType === pt.value ? 'selected' : ''}`}
                                                            onClick={() => setEditingRes({ ...editingRes, advancePayType: pt.value, advanceLast5: '' })}
                                                            style={{ padding: '8px 4px', fontSize: 10 }}>
                                                            <div style={{ fontSize: 16 }}>{pt.icon}</div>
                                                            <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>{pt.label}</div>
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="form-row-2" style={{ marginTop: 10 }}>
                                                    {['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(editingRes.advancePayType) && (
                                                        <div className="field" style={{ marginBottom: 0 }}>
                                                            <label>Reference (Last 5 Digits) *</label>
                                                            <input value={editingRes.advanceLast5} onChange={e => setEditingRes({ ...editingRes, advanceLast5: e.target.value.slice(-5) })} placeholder="e.g. 43210" maxLength={5} />
                                                        </div>
                                                    )}
                                                    <div className="field" style={{ marginBottom: 0 }}>
                                                        <label>Remarks (optional)</label>
                                                        <input value={editingRes.advanceNote} onChange={e => setEditingRes({ ...editingRes, advanceNote: e.target.value })} placeholder="Reference / note" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {editingPayments.length > 0 && (
                                            <div style={{ marginTop: 16, background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                                                    💰 Payment History — correct a wrongly-tagged entry directly
                                                </div>
                                                {editingPayments.map(p => {
                                                    const isEditing = correctingPaymentId === p.id;
                                                    const ptDef = PAYMENT_TYPES.find(pt => pt.value === p.type);
                                                    return (
                                                        <div key={p.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                                                            {!isEditing ? (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                                                    <div>
                                                                        <strong>{Number(p.amount) < 0 ? '− ' : ''}{fmt(Math.abs(Number(p.amount)))}</strong>
                                                                        <span style={{ color: 'var(--muted)', marginLeft: 8 }}>
                                                                            {ptDef?.icon} {ptDef?.label || p.type}{p.last5 ? ` ···${p.last5}` : ''}
                                                                        </span>
                                                                        {p.note && <div style={{ color: 'var(--muted2)', marginTop: 2 }}>{p.note}</div>}
                                                                    </div>
                                                                    {(!isBilled || isAdmin) && <button type="button" className="btn btn-sm btn-ghost" onClick={() => {
                                                                        setCorrectingPaymentId(p.id);
                                                                        setCorrectionDraft({ type: p.type, amount: Math.abs(Number(p.amount)), isRefund: Number(p.amount) < 0, last5: p.last5 || '', note: p.note || '' });
                                                                    }}>✏️ Correct</button>}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <div className="pay-type-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 8 }}>
                                                                        {PAYMENT_TYPES.map(pt => (
                                                                            <button key={pt.value} type="button"
                                                                                className={`pay-type-btn ${correctionDraft.type === pt.value ? 'selected' : ''}`}
                                                                                onClick={() => setCorrectionDraft(d => ({ ...d, type: pt.value, last5: '' }))}
                                                                                style={{ padding: '6px 4px', fontSize: 9 }}>
                                                                                <div style={{ fontSize: 14 }}>{pt.icon}</div>
                                                                                <div style={{ fontSize: 8, fontWeight: 700, marginTop: 1 }}>{pt.label}</div>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                    <div className="form-row-2">
                                                                        <div className="field" style={{ marginBottom: 8 }}>
                                                                            <label>{correctionDraft.isRefund ? 'Refund Amount (₹)' : 'Amount (₹)'}</label>
                                                                            <input type="number" value={correctionDraft.amount} onChange={e => setCorrectionDraft(d => ({ ...d, amount: e.target.value }))} />
                                                                        </div>
                                                                        {['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(correctionDraft.type) && (
                                                                            <div className="field" style={{ marginBottom: 8 }}>
                                                                                <label>Reference (Last 5) *</label>
                                                                                <input value={correctionDraft.last5} onChange={e => setCorrectionDraft(d => ({ ...d, last5: e.target.value.slice(-5) }))} maxLength={5} />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="field" style={{ marginBottom: 8 }}>
                                                                        <label>Remarks</label>
                                                                        <input value={correctionDraft.note} onChange={e => setCorrectionDraft(d => ({ ...d, note: e.target.value }))} />
                                                                    </div>
                                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setCorrectingPaymentId(null)}>Cancel</button>
                                                                        <button type="button" className="btn btn-sm btn-gold" onClick={async () => {
                                                                            if (['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(correctionDraft.type) && !correctionDraft.last5) {
                                                                                await notify('Enter the reference (last 5 digits) for this non-cash payment.');
                                                                                return;
                                                                            }
                                                                            if (!Number.isFinite(Number(correctionDraft.amount)) || Number(correctionDraft.amount) <= 0) {
                                                                                await notify('Enter a valid amount.');
                                                                                return;
                                                                            }
                                                                            try {
                                                                                // A refund is stored as a negative amount — keep its sign.
                                                                                const signedAmount = correctionDraft.isRefund ? -Math.abs(Number(correctionDraft.amount)) : Number(correctionDraft.amount);
                                                                                const amountDelta = signedAmount - Number(p.amount);
                                                                                const { isRefund, ...draft } = correctionDraft;
                                                                                await paymentsApi.update(p.id, { ...draft, amount: signedAmount });
                                                                                setEditingPayments(prev => prev.map(row => row.id === p.id ? { ...row, ...draft, amount: signedAmount } : row));
                                                                                setEditingRes(prev => prev ? { ...prev, advancePaid: Math.max(0, Number(prev.advancePaid) + amountDelta), originalAdvancePaid: Math.max(0, Number(prev.originalAdvancePaid) + amountDelta) } : prev);
                                                                                setCorrectingPaymentId(null);
                                                                                onSuccess?.('Payment entry corrected.');
                                                                            } catch (err) { await notify(err.message); }
                                                                        }}>Save Correction</button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className="field" style={{ marginTop: 16 }}>
                                            <label>Damage Note</label>
                                            <input disabled={isBilled && !billCorrection} placeholder="Describe damages..." value={editingRes.damageNote || ''} onChange={e => setEditingRes({ ...editingRes, damageNote: e.target.value })} />
                                        </div>
                                        <div className="field" style={{ marginTop: 16 }}>
                                            <label>Internal Notes</label>
                                            <textarea value={editingRes.notes || ''} onChange={e => setEditingRes({ ...editingRes, notes: e.target.value })} />
                                        </div>
                                        <div className="field" style={{ marginTop: 16 }}>
                                            <label>Current Status</label>
                                            <select disabled={isBilled} value={editingRes.status} onChange={e => setEditingRes({ ...editingRes, status: e.target.value })}>
                                                <option value="confirmed">Confirmed (Pre-booking)</option>
                                                <option value="checked-in">Checked In</option>
                                                <option value="cancelled">Cancelled</option>
                                                {editingRes.status === 'checked-out' && (
                                                    <option value="checked-out" disabled>Checked Out (Already Billed)</option>
                                                )}
                                            </select>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: isBilled ? '1fr 1.5fr 1.5fr' : '1fr 2fr', gap: 12, marginTop: 32, paddingBottom: 20 }}>
                                            <button type="button" className="btn btn-ghost" style={{ height: 48, fontWeight: 700 }} onClick={() => setEditingRes(null)}>Cancel</button>
                                            {isBilled && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost"
                                                    style={{ height: 48, fontWeight: 800, border: '1px solid var(--accent)', color: 'var(--accent)', justifyContent: 'center' }}
                                                    onClick={async () => {
                                                        try {
                                                            const resBillingType = editingRes.billingType || editingRes.billing_type || 'non-gst';
                                                            let invs = await invoicesApi.forReservation(editingRes.id);
                                                            if (!invs || invs.length === 0) {
                                                                // Checkout marks the reservation checked-out before saving the
                                                                // invoice as a separate request — a network drop in between
                                                                // leaves a billed stay with no invoice row. Issue the missing
                                                                // invoice now (server assigns the number and caps the amounts at
                                                                // the reservation's own totals), then fall through to generate
                                                                // and store its PDF below. Once issued, this invoice (and its
                                                                // number) is found by every later reprint — never re-issued.
                                                                if (editingRes.bulk_group_id) {
                                                                    // A group bill covers every room in the group — rebuilding it
                                                                    // from this one room's record would under-bill the group.
                                                                    return await notify('This room was part of a group checkout and no group invoice was saved. Please contact the admin to issue the group bill.');
                                                                }
                                                                const ok = await confirm({
                                                                    icon: '🧾',
                                                                    title: 'Bill was not saved at checkout',
                                                                    body: `No invoice was recorded for this stay (checkout may have been interrupted). Generate the invoice now for ${fmt(editingRes.totalAmount)}?`,
                                                                    confirmText: 'Generate Invoice',
                                                                });
                                                                if (!ok) return;
                                                                await invoicesApi.create({
                                                                    reservationId: editingRes.id,
                                                                    guestId: editingRes.guestId,
                                                                    hotelId: editingRes.hotelId || editingRes.hotel_id || hotel.id,
                                                                    totalAmount: editingRes.totalAmount,
                                                                    gstAmount: Number(editingRes.gst_amount ?? editingRes.gstAmount ?? 0),
                                                                    billingType: resBillingType
                                                                });
                                                                invs = await invoicesApi.forReservation(editingRes.id);
                                                                if (!invs || invs.length === 0) {
                                                                    return await notify('Could not create the invoice. Please try again.');
                                                                }
                                                            }

                                                            let opened = 0;
                                                            for (const inv of invs) {
                                                                const rawUrl = inv.pdf_url || inv.pdfUrl || inv.pdf_data_url || inv.pdfDataUrl;
                                                                if (rawUrl) {
                                                                    opened++;
                                                                    let blobUrl = rawUrl;
                                                                    if (rawUrl.startsWith('data:')) {
                                                                        const base64Data = rawUrl.split(',')[1];
                                                                        const byteCharacters = atob(base64Data);
                                                                        const byteNumbers = new Array(byteCharacters.length);
                                                                        for (let i = 0; i < byteCharacters.length; i++) {
                                                                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                                                                        }
                                                                        const byteArray = new Uint8Array(byteNumbers);
                                                                        const blob = new Blob([byteArray], { type: 'application/pdf' });
                                                                        blobUrl = URL.createObjectURL(blob);
                                                                    }

                                                                    const pdfWin = window.open(blobUrl, '_blank');
                                                                    if (!pdfWin) {
                                                                        const a = document.createElement('a');
                                                                        a.href = blobUrl;
                                                                        a.download = `Invoice_${inv.invoice_number || 'Bill'}.pdf`;
                                                                        document.body.appendChild(a);
                                                                        a.click();
                                                                        document.body.removeChild(a);
                                                                    }
                                                                }
                                                            }

                                                                if (opened === 0) {
                                                                 const invNumber = (invs && invs[0] && invs[0].invoice_number) || null;
                                                                 let paymentsList = [];
                                                                 try { paymentsList = await paymentsApi.forReservation(editingRes.id); } catch (_) {}
                                                                 let billDesign = {};
                                                                 try { billDesign = await billSettingsApi.get(hotel.id); } catch (_) {}
                                                                 let guestData = {};
                                                                 if (editingRes.guestId) {
                                                                     try { guestData = await guestsApi.get(editingRes.guestId); } catch (_) {}
                                                                 }

                                                                 const { pdfOutput, pdfDataUrl } = await generateInvoicePDF({
                                                                     reservation: editingRes,
                                                                     guestData: { ...editingRes, ...guestData },
                                                                     hotelData: hotel,
                                                                     billDesign,
                                                                     paymentsList: paymentsList || [],
                                                                     invoiceNumber: invNumber,
                                                                     billingType: resBillingType
                                                                 });

                                                                 if (invs && invs[0] && invs[0].id) {
                                                                     try { await invoicesApi.update(invs[0].id, { pdfUrl: pdfDataUrl }); } catch (_) {}
                                                                 }

                                                                 const generatedBlobUrl = URL.createObjectURL(pdfOutput);
                                                                 const pdfWin = window.open(generatedBlobUrl, '_blank');
                                                                 if (!pdfWin) {
                                                                     const a = document.createElement('a');
                                                                     a.href = generatedBlobUrl;
                                                                     a.download = `Invoice_${invNumber || 'Bill'}.pdf`;
                                                                     document.body.appendChild(a);
                                                                     a.click();
                                                                     document.body.removeChild(a);
                                                                 }
                                                             } 
                                                        } catch (e) {
                                                            await notify('Error loading bill: ' + (e.message || 'Server error'));
                                                        }
                                                    }}
                                                >
                                                    🖨️ Re-print Bill
                                                </button>
                                            )}
                                            <button className="btn btn-gold" style={{ height: 48, fontWeight: 800 }}>
                                                {isBilled && billCorrection ? 'Save Bill Correction ✓' : isBilled ? 'Save Guest Details ✓' : 'Save Changes ✓'}
                                            </button>
                                        </div>
                                    </>
                                );
                            })()}
                        </form>
                        )}
                    </div>
                </div>
            )}

            {/* DESIGNER CONFIRM POPUP (MODERN) */}
            {confirmModal && (
                <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', zIndex: 3000 }}>
                    <div className="modal-content" style={{ 
                        maxWidth: 400, 
                        borderRadius: 28, 
                        padding: '40px 32px', 
                        textAlign: 'center', 
                        boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
                        border: 'none',
                        animation: 'modalSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}>
                        <div style={{ fontSize: 56, marginBottom: 24 }}>{confirmModal.icon}</div>
                        <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 16, color: 'var(--text)' }}>{confirmModal.title}</h2>
                        <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 32 }}>{confirmModal.message}</p>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <button 
                                className="btn btn-ghost" 
                                style={{ borderRadius: 16, height: 54, fontWeight: 700, fontSize: 16, border: '1.5px solid var(--border)', background: 'white' }}
                                onClick={() => setConfirmModal(null)}
                            >
                                Cancel
                            </button>
                            <button 
                                className={`btn btn-${confirmModal.type || 'teal'}`} 
                                style={{ 
                                    borderRadius: 16, 
                                    height: 54, 
                                    fontWeight: 800, 
                                    fontSize: 16,
                                    background: confirmModal.type === 'danger' ? 'var(--danger)' : 'var(--success)',
                                    color: 'white',
                                    boxShadow: `0 10px 20px ${confirmModal.type === 'danger' ? 'rgba(231, 76, 60, 0.3)' : 'rgba(46, 204, 113, 0.3)'}` 
                                }}
                                onClick={confirmModal.onConfirm}
                            >
                                {confirmModal.confirmText || 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes modalSlideUp {
                    from { opacity: 0; transform: translateY(20px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
}
